import type { ObstacleKind, ThreatKind, Vec3 } from '../game/models';

export interface WireModel {
  vertices: readonly Vec3[];
  edges: readonly (readonly [number, number])[];
}

const boxVertices = (halfX: number, height: number, halfZ: number): Vec3[] => [
  { x: -halfX, y: 0, z: -halfZ },
  { x: halfX, y: 0, z: -halfZ },
  { x: halfX, y: 0, z: halfZ },
  { x: -halfX, y: 0, z: halfZ },
  { x: -halfX, y: height, z: -halfZ },
  { x: halfX, y: height, z: -halfZ },
  { x: halfX, y: height, z: halfZ },
  { x: -halfX, y: height, z: halfZ },
];

const BOX_EDGES: readonly (readonly [number, number])[] = [
  [0, 1], [1, 2], [2, 3], [3, 0],
  [4, 5], [5, 6], [6, 7], [7, 4],
  [0, 4], [1, 5], [2, 6], [3, 7],
];

export const OBSTACLE_MODELS: Record<ObstacleKind, WireModel> = {
  barrier: {
    vertices: boxVertices(4.5, 2.7, 1.4),
    edges: BOX_EDGES,
  },
  prism: {
    vertices: [
      { x: -3.2, y: 0, z: -2.6 }, { x: 3.2, y: 0, z: -2.6 },
      { x: 3.2, y: 0, z: 2.6 }, { x: -3.2, y: 0, z: 2.6 },
      { x: -3.2, y: 3.7, z: 0 }, { x: 3.2, y: 3.7, z: 0 },
    ],
    edges: [[0, 1], [1, 2], [2, 3], [3, 0], [0, 4], [3, 4], [1, 5], [2, 5], [4, 5]],
  },
  tetra: {
    vertices: [
      { x: -3, y: 0, z: -2.5 }, { x: 3, y: 0, z: -2.5 },
      { x: 0, y: 0, z: 3 }, { x: 0, y: 4.8, z: 0 },
    ],
    edges: [[0, 1], [1, 2], [2, 0], [0, 3], [1, 3], [2, 3]],
  },
  relay: {
    vertices: [
      ...boxVertices(1.7, 5.2, 1.7),
      { x: 0, y: 8.2, z: 0 },
      { x: -3.2, y: 6.6, z: 0 },
      { x: 3.2, y: 6.6, z: 0 },
    ],
    edges: [...BOX_EDGES, [4, 8], [5, 8], [6, 8], [7, 8], [9, 10]],
  },
};

export const THREAT_MODELS: Record<ThreatKind, WireModel> = {
  scout: {
    vertices: [
      ...boxVertices(2.4, 1.25, 3),
      { x: -1.4, y: 1.25, z: -1.2 }, { x: 1.4, y: 1.25, z: -1.2 },
      { x: 1.4, y: 2.25, z: 1.2 }, { x: -1.4, y: 2.25, z: 1.2 },
      { x: 0, y: 1.85, z: 5.3 },
    ],
    edges: [
      ...BOX_EDGES,
      [8, 9], [9, 10], [10, 11], [11, 8], [8, 10], [9, 11],
      [10, 12], [11, 12], [0, 2], [1, 3],
    ],
  },
  siege: {
    vertices: [
      ...boxVertices(3, 1.45, 3.5),
      { x: -1.8, y: 1.45, z: -1.4 }, { x: 1.8, y: 1.45, z: -1.4 },
      { x: 2.2, y: 2.65, z: 1.5 }, { x: -2.2, y: 2.65, z: 1.5 },
      { x: 0, y: 2.15, z: 6.4 }, { x: 0, y: 3.45, z: 0 },
    ],
    edges: [
      ...BOX_EDGES,
      [8, 9], [9, 10], [10, 11], [11, 8], [8, 10], [9, 11],
      [10, 12], [11, 12], [8, 13], [9, 13], [10, 13], [11, 13],
      [0, 2], [1, 3], [4, 6], [5, 7],
    ],
  },
  missile: {
    vertices: [
      { x: 0, y: 0, z: 3.4 }, { x: -1.25, y: -0.7, z: -1.7 },
      { x: 1.25, y: -0.7, z: -1.7 }, { x: 0, y: 1.05, z: -1.2 },
      { x: -2.2, y: 0, z: -2.2 }, { x: 2.2, y: 0, z: -2.2 },
      { x: 0, y: 0, z: -3.2 },
    ],
    edges: [[0, 1], [0, 2], [0, 3], [1, 2], [2, 3], [3, 1], [1, 4], [2, 5], [4, 6], [5, 6]],
  },
};

export const SKIMMER_MODEL: WireModel = {
  vertices: [
    { x: -4, y: 0, z: 0 }, { x: -2.1, y: -0.65, z: -1.1 },
    { x: 2.1, y: -0.65, z: -1.1 }, { x: 4, y: 0, z: 0 },
    { x: 2.1, y: -0.65, z: 1.1 }, { x: -2.1, y: -0.65, z: 1.1 },
    { x: -1.45, y: 0.1, z: 0 }, { x: 0, y: 1.15, z: 0 }, { x: 1.45, y: 0.1, z: 0 },
  ],
  edges: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0], [6, 7], [7, 8], [8, 6], [0, 3], [1, 4], [2, 5]],
};
